/**
 * 案例库: every system case, searched, filtered and opened one at a time.
 * The request is one subject; the search follows it.
 */

import { CoachingNavParams } from '@euler/app/flows/coaching';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import {
  CoachingCaseBrowseRequest,
  CoachingNamedItem,
  kCoachingKeywordMaxLength,
} from '@euler/model/coaching';
import { useBehaviorSubject } from '@euler/utils/hooks/useBehaviorSubject';
import { useCreation } from '@euler/utils/hooks/useCreation';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useMemo, useState } from 'react';
import { BehaviorSubject, Subject } from 'rxjs';
import { CaseFilterSheet } from '../../components/CaseFilterSheet';
import {
  caseFilterSections,
  clearCaseFilters,
  selectedCaseFilters,
  toggleCaseFilter,
  withCaseFiltersOf,
} from '../../functions/caseFilters';
import { useCaseSearch } from '../../functions/useCaseSearch';
import { useCoachingCatalogue } from '../../functions/useCoachingCatalogue';
import { CatalogView } from './CatalogView';

/** Cases a page, in one column. */
const kPageSize = 10;
/** Cases a page, in two columns. */
const kWidePageSize = 20;

export const CoachingCatalogScreen = wrapNavigatorScreen(
  ({
    collection,
    tag,
    tagName,
  }: {
    collection?: string;
    /** A tag tapped on a case: its slug, and its name to show. */
    tag?: string;
    tagName?: string;
  }) => {
    const navigation = useNavigation<StackNavigationProp<CoachingNavParams>>();
    // The route seeds the request once; the page owns it after.
    const [initial] = useState<CoachingCaseBrowseRequest>(() => ({
      collection,
      tags: tag ? [tag] : undefined,
    }));
    // The route's tag may not be among the catalogue's most used ones.
    const [knownTags] = useState<CoachingNamedItem[]>(() =>
      tag ? [{ slug: tag, name: tagName ?? tag }] : [],
    );
    const request$ = useCreation(
      () => new BehaviorSubject<CoachingCaseBrowseRequest>(initial),
      [initial],
    );
    const [request] = useBehaviorSubject(request$);
    const retry$ = useCreation(() => new Subject<void>(), []);
    // A page fills one column on a phone, two rows of two on a wide screen.
    const wide = useIsDesktopLayout();
    const { state: search, loadMore } = useCaseSearch(
      request$,
      retry$,
      wide ? kWidePageSize : kPageSize,
    );
    const catalogue = useCoachingCatalogue();
    const [filtersOpen, setFiltersOpen] = useState(false);
    // Built from the subject's current value: two taps before a re-render
    // must both land.
    const changeRequest = (
      change: (current: CoachingCaseBrowseRequest) => CoachingCaseBrowseRequest,
    ) => request$.next(change(request$.getValue()));
    const clearFilters = () => changeRequest(clearCaseFilters);
    const selectedFilters = useMemo(
      () =>
        selectedCaseFilters(
          request,
          caseFilterSections(catalogue, request.tags ?? [], knownTags),
        ),
      [request, catalogue, knownTags],
    );
    return (
      <>
        <CatalogView
          request={request}
          search={search}
          catalogue={catalogue}
          selectedFilters={selectedFilters}
          onKeyword={keyword =>
            changeRequest(current => ({
              ...current,
              keyword: keyword.slice(0, kCoachingKeywordMaxLength) || undefined,
            }))
          }
          onCategory={category =>
            changeRequest(current => ({
              ...current,
              category: category ?? undefined,
            }))
          }
          onCollection={item =>
            changeRequest(current => ({ ...current, collection: item?.slug }))
          }
          onOpenCollection={item =>
            navigation.push('collection', { slug: item.slug })
          }
          onAllCollections={() => navigation.push('collections', {})}
          onOpenFilters={() => setFiltersOpen(true)}
          onClearFilters={clearFilters}
          onRemoveFilter={(key, value) =>
            changeRequest(current => toggleCaseFilter(current, key, value))
          }
          onOpen={card => navigation.navigate('case', { caseId: card.id })}
          onRetry={() => retry$.next()}
          onLoadMore={loadMore}
        />
        <CaseFilterSheet
          visible={filtersOpen}
          request={request}
          catalogue={catalogue}
          knownTags={knownTags}
          onApply={draft => {
            changeRequest(current => withCaseFiltersOf(current, draft));
            setFiltersOpen(false);
          }}
          onClose={() => setFiltersOpen(false)}
        />
      </>
    );
  },
  { title: '案例库' },
);
